import { type ComponentProps } from 'react'
import Markdown, { type Components } from 'react-markdown'

import { cn } from '@/lib/utils.ts'

const WEB_LINK = /^https?:\/\//i

const webLinkOnly = (url: string) => (WEB_LINK.test(url) ? url : null)

const Heading = ({ children }: ComponentProps<'h4'>) => (
  <h4 className="mt-1 text-[15px] font-bold text-gray-900">{children}</h4>
)

const components: Components = {
  h1: Heading,
  h2: Heading,
  h3: Heading,
  h4: Heading,
  h5: Heading,
  h6: Heading,
  p: ({ children }) => <p>{children}</p>,
  ol: ({ children }) => <ol className="flex list-decimal flex-col gap-2.5 pl-[22px]">{children}</ol>,
  ul: ({ children }) => <ul className="flex list-disc flex-col gap-1 pl-[18px]">{children}</ul>,
  li: ({ children }) => <li className="[&>ol]:mt-1.5 [&>ul]:mt-1.5">{children}</li>,
  a: ({ href, children }) =>
    href ? (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:text-blue-600">
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  img: ({ alt }) => <span>{alt}</span>,
  code: ({ children }) => (
    <code className="rounded-[3px] bg-gray-100 px-1 py-px font-mono text-[0.9em] wrap-anywhere">{children}</code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-[4px] bg-gray-100 p-3 [&>code]:bg-transparent [&>code]:p-0">{children}</pre>
  ),
}

export interface SupportDiagnosticMarkdownProps {
  readonly markdown: string
  readonly className?: string
}

export const SupportDiagnosticMarkdown = ({ markdown, className }: SupportDiagnosticMarkdownProps) => (
  <div className={cn('flex flex-col gap-2.5 text-base leading-normal break-words text-gray-800', className)}>
    <Markdown urlTransform={webLinkOnly} components={components}>
      {markdown}
    </Markdown>
  </div>
)
